import type { IScannerControls } from "@zxing/browser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Clock3,
  Coins,
  Compass,
  Copy,
  CreditCard,
  Crosshair,
  Gift,
  Heart,
  List,
  LoaderCircle,
  LocateFixed,
  Map,
  MapPin,
  Navigation,
  Pencil,
  Phone,
  Plus,
  QrCode,
  RefreshCw,
  RotateCcw,
  Search,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  VideoOff,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { type SyntheticEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  type BusinessLocationInput,
  type BusinessSubscription,
  type GeocodeCandidate,
  type MarketplaceBusiness,
  type MarketplaceLocation,
  type TenantBusinessLocation,
  confirmMemberScan,
  fetchDigitalMemberCard,
  fetchMarketplaceBusiness,
  fetchMarketplaceBusinesses,
  fetchMarketplaceBusinessTypes,
  fetchTenantBusinessLocations,
  fetchBusinessSubscription,
  geocodeBusinessLocation,
  messageFromError,
  reissueDigitalMemberCard,
  resolveMemberScan,
  saveTenantBusinessLocation,
} from "../api";
import {
  activeCameraDeviceId,
  cameraOptions,
  canRetryWithAutomaticCamera,
  describeCameraFailure,
  rearCameraConstraints,
} from "../camera";
import { AppAlert } from "../components/AppAlertDialog";
import { NepalAddressFields } from "../components/NepalAddressFields";
import {
  SubscriptionUpgradeDialog,
  type SubscriptionUpgradeReason,
} from "../components/SubscriptionUpgradeDialog";
import { PlatformTierCard } from "../components/PlatformTierCard";
import { limitUpgradeReason } from "../subscription-access";
import "./loyalty-experience.css";

const number = new Intl.NumberFormat("en-US");
const titleCase = (value: string) =>
  value
    .replaceAll("_", " ")
    .toLocaleLowerCase()
    .replace(/\b\w/g, (letter) => letter.toLocaleUpperCase());
const memberSinceYear = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : String(parsed.getFullYear());
};
const address = (location: MarketplaceLocation | TenantBusinessLocation) =>
  [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.province,
    location.postalCode,
    location.country,
  ]
    .filter(Boolean)
    .join(", ");
type MappedLocation = MarketplaceLocation & { latitude: number; longitude: number };
const hasCoordinates = (location: MarketplaceLocation): location is MappedLocation =>
  location.latitude !== null && location.longitude !== null;
const mapEmbed = (latitude: number, longitude: number) => {
  const delta = 0.012;
  const bbox = [longitude - delta, latitude - delta, longitude + delta, latitude + delta].join(",");
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(`${String(latitude)},${String(longitude)}`)}`;
};
const directionsUrl = (location: Pick<MarketplaceLocation, "latitude" | "longitude">) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${String(location.latitude)},${String(location.longitude)}`)}`;
const readStoredStringArray = (key: string) => {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(value) && value.every((item) => typeof item === "string") ? value : [];
  } catch {
    return [];
  }
};
const isOpenNow = (location: MarketplaceLocation) => {
  try {
    const now = new Date();
    const day = new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      timeZone: location.timezone,
    })
      .format(now)
      .toLocaleLowerCase();
    const hours = location.openingHours[day];
    if (!hours) return null;
    if (hours.toLocaleLowerCase() === "closed") return false;
    const match = hours.match(/(\d{1,2}):(\d{2})\s*[\u2011\u2013\u2014-]\s*(\d{1,2}):(\d{2})/);
    if (!match) return null;
    const parts = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone: location.timezone,
    }).formatToParts(now);
    const current =
      Number(parts.find((part) => part.type === "hour")?.value ?? 0) * 60 +
      Number(parts.find((part) => part.type === "minute")?.value ?? 0);
    const opens = Number(match[1]) * 60 + Number(match[2]);
    const closes = Number(match[3]) * 60 + Number(match[4]);
    return closes < opens
      ? current >= opens || current < closes
      : current >= opens && current < closes;
  } catch {
    return null;
  }
};

function ExperienceState({
  title,
  detail,
  retry,
}: {
  title: string;
  detail: string;
  retry?: () => void;
}) {
  return (
    <>
      {retry && <AppAlert message={detail} title={title} />}
      <div className="lx-state">
        <span>{retry ? <CircleHelp /> : <LoaderCircle className="lx-spin" />}</span>
        <strong>{title}</strong>
        <p>{retry ? "Try loading this area again." : detail}</p>
        {retry && (
          <button className="button button--outline button--compact" type="button" onClick={retry}>
            <RefreshCw /> Try again
          </button>
        )}
      </div>
    </>
  );
}

export function DigitalCardView() {
  const queryClient = useQueryClient();
  const cardQuery = useQuery({
    queryKey: ["digital-member-card"],
    queryFn: fetchDigitalMemberCard,
    refetchInterval: 90_000,
    refetchIntervalInBackground: false,
  });
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const reissue = useMutation({
    mutationFn: reissueDigitalMemberCard,
    onSuccess: (card) => {
      queryClient.setQueryData(["digital-member-card"], card);
      setErrorMessage("");
      setMessage("All previous QR presentations are revoked. A fresh QR is ready.");
    },
    onError: (error) => {
      setMessage("");
      setErrorMessage(messageFromError(error));
    },
  });
  if (cardQuery.isLoading)
    return (
      <ExperienceState
        title="Preparing your digital card"
        detail="Creating a secure member credential for this device."
      />
    );
  if (cardQuery.isError || !cardQuery.data)
    return (
      <ExperienceState
        title="Your card is temporarily unavailable"
        detail={messageFromError(cardQuery.error)}
        retry={() => {
          void cardQuery.refetch();
        }}
      />
    );
  const card = cardQuery.data;
  return (
    <main className="cx-page lx-card-page">
      <section className="lx-page-heading">
        <div>
          <p className="lx-kicker">DIGITAL REWARDS CARD</p>
          <h1>Your Mero Perks identity, ready to scan.</h1>
          <p>
            Open this screen only when staff is ready. The QR expires quickly, works once, and
            contains no email, phone, birthday, or address.
          </p>
        </div>
        <CreditCard />
      </section>
      {message && (
        <div className="lx-message" role="status">
          {message}
        </div>
      )}
      {errorMessage && (
        <AppAlert
          message={errorMessage}
          onDismiss={() => {
            setErrorMessage("");
          }}
        />
      )}
      <section className="lx-card-stage">
        <div className="lx-card-identity-stack">
          <PlatformTierCard
            className="lx-platform-member-card"
            tierCode={card.tier.code}
            tierName={card.tier.name}
            loading="eager"
            member={{
              name: card.customerName,
              memberId: card.memberNumber,
              memberSince: memberSinceYear(card.issuedAt),
            }}
          />
          <section className={`lx-platform-member-qr ${expanded ? "is-expanded" : ""}`}>
            <div className="lx-platform-member-qr__heading">
              <span>
                <ShieldCheck /> Verified member
              </span>
              <strong>{card.tier.name}</strong>
            </div>
            <div className="lx-card-qr">
              <QRCodeSVG
                value={card.qrPayload}
                size={expanded ? 208 : 148}
                level="H"
                bgColor="#ffffff"
                fgColor="#122937"
              />
              <small>One-time QR · version {String(card.version)}</small>
              <small>
                Expires{" "}
                {new Intl.DateTimeFormat("en", {
                  hour: "numeric",
                  minute: "2-digit",
                  second: "2-digit",
                }).format(new Date(card.qrExpiresAt))}
              </small>
            </div>
          </section>
        </div>
        <aside className="lx-card-guide">
          <p className="lx-kicker">USE YOUR CARD</p>
          <h2>Fast at checkout. Safe by design.</h2>
          <ol>
            <li>
              <span>1</span>
              <div>
                <strong>Open a fresh QR</strong>
                <p>Wait until the cashier is ready, then show this screen.</p>
              </div>
            </li>
            <li>
              <span>2</span>
              <div>
                <strong>Confirm the business</strong>
                <p>Scanning identifies your membership; it never silently redeems a reward.</p>
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <strong>One scan only</strong>
                <p>The QR expires quickly and is rejected after its first accepted scan.</p>
              </div>
            </li>
          </ol>
          <div className="lx-card-actions">
            <button
              className="button button--primary"
              type="button"
              onClick={() => {
                setExpanded((value) => !value);
              }}
            >
              <QrCode /> {expanded ? "Fit QR" : "Enlarge QR"}
            </button>
            <button
              className="button button--outline"
              type="button"
              onClick={() => {
                void cardQuery.refetch();
              }}
            >
              <RefreshCw /> Refresh QR
            </button>
          </div>
          <button
            className="lx-reissue"
            type="button"
            disabled={reissue.isPending}
            onClick={() => {
              if (
                window.confirm(
                  "Reissue this card? Every current QR and pending scan will stop working immediately.",
                )
              )
                reissue.mutate();
            }}
          >
            <RotateCcw /> {reissue.isPending ? "Reissuing…" : "Lost or copied? Reissue securely"}
          </button>
        </aside>
      </section>
    </main>
  );
}

function BusinessLogo({ business }: { business: MarketplaceBusiness }) {
  return (
    <span
      className="lx-business-logo"
      style={{ "--business-color": business.primaryColor } as React.CSSProperties}
    >
      {business.logoUrl ? (
        <img src={business.logoUrl} alt="" />
      ) : (
        business.name
          .split(/\s+/)
          .slice(0, 2)
          .map((part) => part[0])
          .join("")
      )}
    </span>
  );
}

function LocationActions({ location }: { location: MarketplaceLocation }) {
  const copy = async () => {
    await navigator.clipboard.writeText(address(location));
  };
  return (
    <div className="lx-location-actions">
      {hasCoordinates(location) && (
        <a
          className="button button--primary button--compact"
          href={directionsUrl(location)}
          target="_blank"
          rel="noreferrer"
        >
          <Navigation /> Directions
        </a>
      )}
      {location.phone && (
        <a className="button button--outline button--compact" href={`tel:${location.phone}`}>
          <Phone /> Call
        </a>
      )}
      <button
        className="button button--quiet button--compact"
        type="button"
        onClick={() => {
          void copy();
        }}
      >
        <Copy /> Copy address
      </button>
    </div>
  );
}

function BusinessDetail({
  business,
  coordinates,
  saved,
  toggleSaved,
  close,
}: {
  business: MarketplaceBusiness;
  coordinates: { latitude: number; longitude: number } | null;
  saved: boolean;
  toggleSaved: () => void;
  close: () => void;
}) {
  const detail = useQuery({
    queryKey: ["marketplace-business", business.slug, coordinates],
    queryFn: () => fetchMarketplaceBusiness(business.slug, coordinates ?? undefined),
  });
  const [locationId, setLocationId] = useState(business.locations[0]?.publicId ?? "");
  const [shareStatus, setShareStatus] = useState("");
  const data = detail.data ?? business;
  const selected = data.locations.find((item) => item.publicId === locationId) ?? data.locations[0];
  useEffect(() => {
    if (!locationId && data.locations[0]) setLocationId(data.locations[0].publicId);
  }, [data.locations, locationId]);
  const share = async () => {
    const shareData = {
      title: `${data.name} on Mero Perks`,
      text: `See rewards and locations for ${data.name} on Mero Perks.`,
      url: `${window.location.origin}/marketplace`,
    };
    try {
      const shareMethod = Reflect.get(navigator, "share") as
        ((data: ShareData) => Promise<void>) | undefined;
      if (typeof shareMethod === "function") {
        await shareMethod.call(navigator, shareData);
        setShareStatus("Business shared.");
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setShareStatus("Marketplace link copied.");
      }
    } catch {
      setShareStatus("Sharing was cancelled.");
    }
  };
  return (
    <div
      className="lx-detail"
      role="dialog"
      aria-modal="true"
      aria-labelledby="business-detail-title"
    >
      <button
        className="lx-detail__scrim"
        type="button"
        aria-label="Close business details"
        onClick={close}
      />
      <section className="lx-detail__panel">
        <button className="lx-detail__close" type="button" onClick={close}>
          <X />
          <span>Close</span>
        </button>
        <header>
          <BusinessLogo business={data} />
          <div>
            <p className="lx-kicker">{titleCase(data.businessType)}</p>
            <h2 id="business-detail-title">{data.name}</h2>
            <p>
              {data.rewardCount} available {data.rewardCount === 1 ? "reward" : "rewards"} ·{" "}
              {data.locations.length} {data.locations.length === 1 ? "location" : "locations"}
            </p>
          </div>
          <button
            className={saved ? "is-saved" : ""}
            type="button"
            aria-label={saved ? `Remove ${data.name} from saved businesses` : `Save ${data.name}`}
            aria-pressed={saved}
            onClick={toggleSaved}
          >
            <Heart />
          </button>
          <button type="button" aria-label={`Share ${data.name}`} onClick={() => void share()}>
            <Share2 />
          </button>
        </header>
        {shareStatus && (
          <p className="lx-action-status" role="status">
            {shareStatus}
          </p>
        )}
        {detail.isLoading ? (
          <ExperienceState
            title="Loading business details"
            detail="Finding locations, hours, and benefits."
          />
        ) : (
          selected && (
            <>
              <div className="lx-detail__badges">
                <span>
                  <Gift /> {String(data.rewardCount)} rewards
                </span>
                {data.meroCreditsAccepted && (
                  <span>
                    <Coins /> Mero Credits accepted
                  </span>
                )}
                <span>
                  <BadgeCheck /> Mero Tier benefits
                </span>
              </div>
              {hasCoordinates(selected) ? (
                <div className="lx-detail__map">
                  <iframe
                    title={`Map showing ${selected.name}`}
                    loading="lazy"
                    src={mapEmbed(selected.latitude, selected.longitude)}
                  />
                  <p>
                    <MapPin /> Map data © OpenStreetMap contributors
                  </p>
                </div>
              ) : (
                <div className="lx-map-empty lx-map-empty--compact">
                  <MapPin />
                  <strong>Map pin coming soon</strong>
                  <p>The full public address remains available below.</p>
                </div>
              )}
              <label className="lx-location-picker">
                <span>Choose a location</span>
                <select
                  value={selected.publicId}
                  onChange={(event) => {
                    setLocationId(event.target.value);
                  }}
                >
                  {data.locations.map((item) => (
                    <option key={item.publicId} value={item.publicId}>
                      {item.name}
                      {item.distanceKm !== null ? ` · ${item.distanceKm.toFixed(1)} km` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <article className="lx-location-summary">
                <div>
                  <h3>{selected.name}</h3>
                  <p>{address(selected)}</p>
                  {selected.landmarkNote && <small>Near {selected.landmarkNote}</small>}
                </div>
                <div>
                  <span className={isOpenNow(selected) === false ? "is-closed" : "is-open"}>
                    <Clock3 /> {isOpenNow(selected) === false ? "Closed today" : "Hours available"}
                  </span>
                  {selected.distanceKm !== null && (
                    <strong>{selected.distanceKm.toFixed(1)} km away</strong>
                  )}
                </div>
              </article>
              <LocationActions location={selected} />
              <section className="lx-business-rewards">
                <div>
                  <p className="lx-kicker">AVAILABLE PERKS</p>
                  <h3>Rewards worth returning for</h3>
                </div>
                <Link
                  className="button button--primary"
                  to={`/account/offers?business=${encodeURIComponent(data.slug)}`}
                >
                  View {String(data.rewardCount)} rewards <ArrowRight />
                </Link>
              </section>
              <section className="lx-tier-benefits">
                <p className="lx-kicker">MERO TIER BENEFITS</p>
                {(data.tierBenefits ?? []).slice(0, 4).map((benefit) => (
                  <div key={`${benefit.code}-${benefit.title}`}>
                    <BadgeCheck />
                    <span>
                      <strong>
                        {benefit.name}: {benefit.title}
                      </strong>
                      <small>{benefit.description}</small>
                    </span>
                  </div>
                ))}
              </section>
            </>
          )
        )}
      </section>
    </div>
  );
}

function MarketplaceMap({
  businesses,
  selectedBusinessId,
  onSelect,
}: {
  businesses: MarketplaceBusiness[];
  selectedBusinessId: string | null;
  onSelect: (business: MarketplaceBusiness) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerLayerRef = useRef<L.LayerGroup | null>(null);
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
    });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;

    const resizeFrame = window.requestAnimationFrame(() => {
      map.invalidateSize();
    });

    return () => {
      window.cancelAnimationFrame(resizeFrame);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markerLayerRef.current?.remove();
    const markerLayer = L.layerGroup().addTo(map);
    markerLayerRef.current = markerLayer;
    const bounds = L.latLngBounds([]);
    let markerCount = 0;

    for (const business of businesses) {
      for (const location of business.locations.filter(hasCoordinates)) {
        const selected = business.publicId === selectedBusinessId;
        const icon = L.divIcon({
          className: `lx-map-pin${selected ? " is-selected" : ""}`,
          html: '<span class="lx-map-pin__shape"><span class="lx-map-pin__center"></span></span>',
          iconAnchor: [19, 44],
          iconSize: [38, 44],
          tooltipAnchor: [0, -38],
        });
        const marker = L.marker([location.latitude, location.longitude], {
          alt: `${business.name} map pin`,
          icon,
          keyboard: true,
          riseOnHover: true,
          title: business.name,
        }).addTo(markerLayer);
        const tooltip = document.createElement("span");
        tooltip.textContent = business.name;
        marker.bindTooltip(tooltip, {
          className: "lx-map-business-tooltip",
          direction: "top",
          opacity: 1,
          sticky: true,
        });
        marker.on("click", () => {
          onSelectRef.current(business);
        });
        bounds.extend([location.latitude, location.longitude]);
        markerCount += 1;
      }
    }

    if (markerCount === 1) {
      map.setView(bounds.getCenter(), 14);
    } else if (markerCount > 1) {
      map.fitBounds(bounds, { maxZoom: 14, padding: [48, 48] });
    }
  }, [businesses, selectedBusinessId]);

  return (
    <div
      className="lx-map-surface"
      ref={containerRef}
      aria-label="Map of businesses with pinned locations"
    />
  );
}

export function MarketplaceView() {
  const [search, setSearch] = useState("");
  const [types, setTypes] = useState<string[]>(() =>
    readStoredStringArray("mero-marketplace-types"),
  );
  const [savedIds, setSavedIds] = useState<string[]>(() =>
    readStoredStringArray("mero-marketplace-saved"),
  );
  const [openNow, setOpenNow] = useState(false);
  const [sort, setSort] = useState<"recommended" | "name" | "rewards" | "distance">("recommended");
  const [mode, setMode] = useState<"list" | "map">(() =>
    localStorage.getItem("mero-marketplace-mode") === "map" ? "map" : "list",
  );
  const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number } | null>(
    null,
  );
  const [locationMessage, setLocationMessage] = useState("");
  const [locationWarning, setLocationWarning] = useState("");
  const [selected, setSelected] = useState<MarketplaceBusiness | null>(null);
  const typeQuery = useQuery({
    queryKey: ["marketplace-types"],
    queryFn: fetchMarketplaceBusinessTypes,
  });
  const businesses = useQuery({
    queryKey: ["marketplace", search, types, coordinates],
    queryFn: () => fetchMarketplaceBusinesses({ search, types, ...(coordinates ?? {}) }),
  });
  useEffect(() => {
    localStorage.setItem("mero-marketplace-mode", mode);
  }, [mode]);
  useEffect(() => {
    localStorage.setItem("mero-marketplace-types", JSON.stringify(types));
  }, [types]);
  useEffect(() => {
    localStorage.setItem("mero-marketplace-saved", JSON.stringify(savedIds));
  }, [savedIds]);
  const toggleSaved = (businessId: string) => {
    setSavedIds((current) =>
      current.includes(businessId)
        ? current.filter((value) => value !== businessId)
        : [...current, businessId],
    );
  };
  const useLocation = () => {
    setLocationWarning("");
    setLocationMessage(
      "Requesting your location. Mero Perks uses it only to sort this page and does not store it.",
    );
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoordinates({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationMessage("Nearby results are sorted from your current position.");
      },
      () => {
        setLocationMessage("");
        setLocationWarning("Location access was not granted. Search by business or area instead.");
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    );
  };
  const items = businesses.data?.items ?? [];
  const filteredItems = openNow
    ? items.filter((business) =>
        business.locations.some((location) => isOpenNow(location) === true),
      )
    : items;
  const visibleItems = [...filteredItems].sort((left, right) => {
    if (sort === "name") return left.name.localeCompare(right.name);
    if (sort === "rewards") return right.rewardCount - left.rewardCount;
    if (sort === "distance") {
      if (left.nearestDistanceKm === null) return 1;
      if (right.nearestDistanceKm === null) return -1;
      return left.nearestDistanceKm - right.nearestDistanceKm;
    }
    return 0;
  });
  const mappedVisibleItems = visibleItems.filter((business) =>
    business.locations.some(hasCoordinates),
  );
  const selectedMappedBusiness = selected
    ? (mappedVisibleItems.find((business) => business.publicId === selected.publicId) ?? null)
    : null;
  const mapBusiness = selectedMappedBusiness ?? mappedVisibleItems[0] ?? null;
  const mapLocation = mapBusiness?.locations.find(hasCoordinates) ?? null;
  const resultCount = mode === "map" ? mappedVisibleItems.length : visibleItems.length;
  return (
    <main className="cx-page lx-marketplace">
      <section className="lx-marketplace-hero">
        <div>
          <p className="lx-kicker">EXPLORE MERO PERKS</p>
          <h1>Find a place you’ll want to come back to.</h1>
          <p>
            Discover stores, restaurants, cafés, hotels, rewards, and Mero Credits locations near
            you.
          </p>
        </div>
        <div className="lx-marketplace-search">
          <Search />
          <label>
            <span className="sr-only">Search businesses or areas</span>
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder="Search business, category, or area"
            />
          </label>
          {search && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setSearch("");
              }}
            >
              <X />
            </button>
          )}
        </div>
      </section>
      <section className="lx-marketplace-toolbar">
        <div className="lx-filter-scroll" aria-label="Business type filters">
          <button
            className={openNow ? "is-active" : ""}
            type="button"
            aria-pressed={openNow}
            onClick={() => {
              setOpenNow((current) => !current);
            }}
          >
            <Clock3 /> Open now
          </button>
          {(typeQuery.data ?? []).map((item) => {
            const active = types.includes(item.value);
            return (
              <button
                className={active ? "is-active" : ""}
                type="button"
                aria-pressed={active}
                key={item.value}
                onClick={() => {
                  setTypes((current) =>
                    current.includes(item.value)
                      ? current.filter((value) => value !== item.value)
                      : [...current, item.value],
                  );
                }}
              >
                <Store /> {titleCase(item.value)} <span>{String(item.count)}</span>
              </button>
            );
          })}
          {types.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setTypes([]);
              }}
            >
              <X /> Clear
            </button>
          )}
        </div>
        <div className="lx-view-controls">
          <label className="lx-sort-select">
            <span className="sr-only">Sort businesses</span>
            <SlidersHorizontal />
            <select
              value={sort}
              onChange={(event) => {
                setSort(event.target.value as typeof sort);
              }}
            >
              <option value="recommended">Recommended</option>
              <option value="name">Business: A to Z</option>
              <option value="rewards">Most rewards</option>
              <option value="distance">Nearest first</option>
            </select>
            <ChevronDown className="lx-sort-select__chevron" />
          </label>
          <button type="button" onClick={useLocation}>
            <LocateFixed /> Near me
          </button>
          <div role="group" aria-label="Marketplace view">
            <button
              className={mode === "list" ? "is-active" : ""}
              type="button"
              aria-pressed={mode === "list"}
              onClick={() => {
                setMode("list");
              }}
            >
              <List /> List
            </button>
            <button
              className={mode === "map" ? "is-active" : ""}
              type="button"
              aria-pressed={mode === "map"}
              onClick={() => {
                setMode("map");
              }}
            >
              <Map /> Map
            </button>
          </div>
        </div>
      </section>
      {locationMessage && (
        <div className="lx-location-message" role="status">
          <ShieldCheck /> {locationMessage}
        </div>
      )}
      {locationWarning && (
        <AppAlert
          tone="warning"
          message={locationWarning}
          onDismiss={() => {
            setLocationWarning("");
          }}
        />
      )}
      {businesses.isLoading ? (
        <ExperienceState
          title="Finding Mero Perks businesses"
          detail="Loading locations and current reward availability."
        />
      ) : businesses.isError ? (
        <ExperienceState
          title="Marketplace is temporarily unavailable"
          detail={messageFromError(businesses.error)}
          retry={() => {
            void businesses.refetch();
          }}
        />
      ) : (
        <>
          <div className="lx-results-count">
            <strong>
              {number.format(resultCount)} {resultCount === 1 ? "business" : "businesses"}
              {mode === "map" ? " with pinned locations" : ""}
            </strong>
            <span>
              {coordinates ? "Sorted by distance" : "Search an area or use Near me for distance"}
            </span>
          </div>
          {mode === "list" ? (
            <div className="lx-business-grid">
              {visibleItems.map((business) => {
                const nearest = business.locations[0];
                const saved = savedIds.includes(business.publicId);
                return (
                  <article className="lx-business-card" key={business.publicId}>
                    <button
                      className="lx-business-card__save"
                      type="button"
                      aria-label={
                        saved
                          ? `Remove ${business.name} from saved businesses`
                          : `Save ${business.name}`
                      }
                      aria-pressed={saved}
                      onClick={() => {
                        toggleSaved(business.publicId);
                      }}
                    >
                      <Heart />
                    </button>
                    <div
                      className="lx-business-card__cover"
                      style={{ "--business-color": business.primaryColor } as React.CSSProperties}
                    >
                      <BusinessLogo business={business} />
                      <span>{titleCase(business.businessType)}</span>
                    </div>
                    <div className="lx-business-card__body">
                      <h2>{business.name}</h2>
                      <p>
                        <MapPin />{" "}
                        {nearest
                          ? [nearest.city, nearest.province].filter(Boolean).join(", ")
                          : "Locations available"}
                      </p>
                      <div className="lx-business-card__badges">
                        <span>
                          <Gift /> {String(business.rewardCount)} rewards
                        </span>
                        {business.meroCreditsAccepted && (
                          <span>
                            <Coins /> Credits
                          </span>
                        )}
                        {business.nearestDistanceKm !== null && (
                          <span>
                            <Navigation /> {business.nearestDistanceKm.toFixed(1)} km
                          </span>
                        )}
                      </div>
                      <button
                        className="button button--outline button--full"
                        type="button"
                        onClick={() => {
                          setSelected(business);
                        }}
                      >
                        View business <ChevronRight />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <section className="lx-map-layout">
              <div className="lx-map-canvas">
                {mapLocation ? (
                  <MarketplaceMap
                    businesses={mappedVisibleItems}
                    selectedBusinessId={mapBusiness?.publicId ?? null}
                    onSelect={setSelected}
                  />
                ) : (
                  <div className="lx-map-empty">
                    <MapPin />
                    <strong>No businesses with pinned locations</strong>
                    <p>Clear a filter or search another area.</p>
                  </div>
                )}
              </div>
              <div className="lx-map-results">
                <h2>Search this area</h2>
                {mappedVisibleItems.map((business) => (
                  <button
                    className={mapBusiness?.publicId === business.publicId ? "is-active" : ""}
                    type="button"
                    key={business.publicId}
                    onClick={() => {
                      setSelected(business);
                    }}
                  >
                    <BusinessLogo business={business} />
                    <span>
                      <strong>{business.name}</strong>
                      <small>
                        {business.nearestDistanceKm !== null
                          ? `${business.nearestDistanceKm.toFixed(1)} km · `
                          : ""}
                        {String(business.rewardCount)} rewards
                      </small>
                    </span>
                    <ChevronRight />
                  </button>
                ))}
              </div>
            </section>
          )}
          {visibleItems.length === 0 && (
            <div className="lx-empty">
              <Compass />
              <h2>No businesses match yet</h2>
              <p>Clear a filter, search a broader area, or browse without your location.</p>
              <button
                className="button button--primary"
                type="button"
                onClick={() => {
                  setSearch("");
                  setTypes([]);
                  setOpenNow(false);
                }}
              >
                Clear filters
              </button>
            </div>
          )}
        </>
      )}
      {selected && (
        <BusinessDetail
          business={selected}
          coordinates={coordinates}
          saved={savedIds.includes(selected.publicId)}
          toggleSaved={() => {
            toggleSaved(selected.publicId);
          }}
          close={() => {
            setSelected(null);
          }}
        />
      )}
    </main>
  );
}

const emptyLocation = (): BusinessLocationInput => ({
  name: "",
  code: "",
  addressLine1: "",
  addressLine2: null,
  city: "",
  district: null,
  province: null,
  country: "Nepal",
  postalCode: null,
  landmarkNote: null,
  phone: null,
  email: null,
  publicWebsite: null,
  timezone: "Asia/Kathmandu",
  latitude: null,
  longitude: null,
  openingHours: {
    monday: "09:00–18:00",
    tuesday: "09:00–18:00",
    wednesday: "09:00–18:00",
    thursday: "09:00–18:00",
    friday: "09:00–18:00",
    saturday: "10:00–16:00",
    sunday: "Closed",
  },
  isPublic: true,
  status: "active",
  locationType: "STORE",
});

export function TenantLocations() {
  const queryClient = useQueryClient();
  const locations = useQuery({
    queryKey: ["tenant-business-locations"],
    queryFn: fetchTenantBusinessLocations,
  });
  const subscription = useQuery<BusinessSubscription>({
    queryKey: ["tenant-subscription"],
    queryFn: fetchBusinessSubscription,
  });
  const [editingId, setEditingId] = useState<string | undefined>();
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState<BusinessLocationInput>(emptyLocation);
  const [addressQuery, setAddressQuery] = useState("");
  const [candidates, setCandidates] = useState<GeocodeCandidate[]>([]);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [upgradeReason, setUpgradeReason] = useState<SubscriptionUpgradeReason | null>(null);
  const openNewLocation = () => {
    const limit = subscription.data?.accessPlan.limits.locations;
    const locationCount = locations.data?.length ?? 0;
    if (subscription.data && limit !== null && limit !== undefined && locationCount >= limit) {
      setUpgradeReason(limitUpgradeReason(subscription.data, "business locations", limit));
      return;
    }
    setEditingId(undefined);
    setDraft(emptyLocation());
    setAddressQuery("");
    setCandidates([]);
    setFormOpen(true);
  };
  const geocode = useMutation({
    mutationFn: geocodeBusinessLocation,
    onSuccess: (results) => {
      setErrorMessage("");
      setCandidates(results);
    },
    onError: (error) => {
      setMessage("");
      setErrorMessage(messageFromError(error));
    },
  });
  const save = useMutation({
    mutationFn: () => saveTenantBusinessLocation(draft, editingId),
    onSuccess: async () => {
      setErrorMessage("");
      setMessage(editingId ? "Location updated." : "Location published.");
      setEditingId(undefined);
      setFormOpen(false);
      setDraft(emptyLocation());
      setCandidates([]);
      setAddressQuery("");
      await queryClient.invalidateQueries({ queryKey: ["tenant-business-locations"] });
      await queryClient.invalidateQueries({ queryKey: ["tenant-subscription"] });
    },
    onError: (error) => {
      setMessage("");
      setErrorMessage(messageFromError(error));
    },
  });
  const choose = (candidate: GeocodeCandidate) => {
    setDraft((value) => ({
      ...value,
      addressLine1: candidate.addressLine1 || candidate.label,
      city: candidate.city,
      district: candidate.district,
      province: candidate.province,
      country: candidate.country,
      postalCode: candidate.postalCode,
      latitude: candidate.latitude,
      longitude: candidate.longitude,
    }));
    setCandidates([]);
    setAddressQuery(candidate.label);
  };
  const edit = (location: TenantBusinessLocation) => {
    setEditingId(location.publicId);
    setFormOpen(true);
    setDraft({ ...location });
    setAddressQuery(address(location));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <main
      className={`portal-content lx-tenant-locations${formOpen ? " lx-tenant-locations--editor" : ""}`}
    >
      {upgradeReason && <SubscriptionUpgradeDialog reason={upgradeReason} close={() => { setUpgradeReason(null); }} />}
      <section className="welcome-row">
        <div>
          <p className="eyebrow">Locations & map visibility</p>
          <h1>Put every branch on the Mero Perks map</h1>
          <p>
            Confirm a real address and map pin so customers can find, call, and navigate to the
            right location.
          </p>
        </div>
        {formOpen ? (
          <button
            className="button button--outline"
            type="button"
            onClick={() => {
              setEditingId(undefined);
              setDraft(emptyLocation());
              setFormOpen(false);
            }}
          >
            <ChevronRight className="lx-back-chevron" /> Back to locations
          </button>
        ) : (
          <button
            className="button button--primary"
            type="button"
            onClick={openNewLocation}
          >
            <Plus /> Add new Location
          </button>
        )}
      </section>
      {message && (
        <div className="lx-message" role="status">
          {message}
        </div>
      )}
      {errorMessage && (
        <AppAlert
          message={errorMessage}
          onDismiss={() => {
            setErrorMessage("");
          }}
        />
      )}
      {formOpen && (
        <div className="lx-location-admin-grid">
          <form
            className="portal-panel lx-location-form"
            onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="panel-heading">
              <div>
                <p className="eyebrow">{editingId ? "Edit location" : "Add a location"}</p>
                <h2>{editingId ? "Update branch details" : "Search, pin, and publish"}</h2>
              </div>
              {editingId && (
                <button
                  className="button button--quiet button--compact"
                  type="button"
                  onClick={() => {
                    setEditingId(undefined);
                    setDraft(emptyLocation());
                    setFormOpen(false);
                  }}
                >
                  <X /> Cancel
                </button>
              )}
            </div>
            <div className="lx-address-search">
              <label>
                <span>Find the public address</span>
                <div>
                  <Search />
                  <input
                    value={addressQuery}
                    onChange={(event) => {
                      setAddressQuery(event.target.value);
                    }}
                    placeholder="Street, area, city, country"
                  />
                  <button
                    className="button button--primary button--compact"
                    disabled={geocode.isPending || addressQuery.trim().length < 4}
                    type="button"
                    onClick={() => {
                      geocode.mutate(addressQuery);
                    }}
                  >
                    {geocode.isPending ? "Searching…" : "Find"}
                  </button>
                </div>
              </label>
              {candidates.length > 0 && (
                <div
                  className="lx-address-candidates"
                  role="listbox"
                  aria-label="Address candidates"
                >
                  {candidates.map((candidate) => (
                    <button
                      type="button"
                      role="option"
                      aria-selected="false"
                      key={candidate.id}
                      onClick={() => {
                        choose(candidate);
                      }}
                    >
                      <MapPin />
                      <span>
                        <strong>{candidate.addressLine1 || candidate.city}</strong>
                        <small>{candidate.label}</small>
                      </span>
                      <ChevronRight />
                    </button>
                  ))}
                </div>
              )}
              <small>
                Search runs only when you press Find. Geocoding data © OpenStreetMap contributors.
              </small>
            </div>
            <div className="lx-form-grid">
              <label>
                <span>Location name</span>
                <input
                  required
                  value={draft.name}
                  onChange={(event) => {
                    setDraft({ ...draft, name: event.target.value });
                  }}
                  placeholder="Durbar Marg Flagship"
                />
              </label>
              <label>
                <span>Location code</span>
                <input
                  required
                  pattern="[A-Za-z0-9_-]{2,24}"
                  value={draft.code}
                  onChange={(event) => {
                    setDraft({ ...draft, code: event.target.value.toLocaleUpperCase() });
                  }}
                  placeholder="DURBAR-01"
                />
              </label>
              <label className="is-wide">
                <span>Address line 1</span>
                <input
                  required
                  value={draft.addressLine1}
                  onChange={(event) => {
                    setDraft({ ...draft, addressLine1: event.target.value });
                  }}
                />
              </label>
              <label className="is-wide">
                <span>Address line 2 (optional)</span>
                <input
                  value={draft.addressLine2 ?? ""}
                  onChange={(event) => {
                    setDraft({ ...draft, addressLine2: event.target.value || null });
                  }}
                />
              </label>
              <NepalAddressFields
                required
                className="is-wide tenant-location-address-grid"
                value={{
                  province: draft.province ?? "",
                  district: draft.district ?? "",
                  localLevel: draft.city,
                }}
                onChange={({ province, district, localLevel }) => {
                  setDraft({ ...draft, province, district, city: localLevel });
                }}
              />
              <label>
                <span>Country</span>
                <select
                  required
                  value={draft.country || "Nepal"}
                  onChange={(event) => {
                    setDraft({ ...draft, country: event.target.value });
                  }}
                >
                  <option value={draft.country || "Nepal"}>Nepal</option>
                </select>
              </label>
              <label>
                <span>Latitude (optional)</span>
                <input
                  min="-90"
                  max="90"
                  step="0.000001"
                  type="number"
                  value={draft.latitude ?? ""}
                  onChange={(event) => {
                    setDraft({
                      ...draft,
                      latitude: event.target.value ? Number(event.target.value) : null,
                    });
                  }}
                />
              </label>
              <label>
                <span>Longitude (optional)</span>
                <input
                  min="-180"
                  max="180"
                  step="0.000001"
                  type="number"
                  value={draft.longitude ?? ""}
                  onChange={(event) => {
                    setDraft({
                      ...draft,
                      longitude: event.target.value ? Number(event.target.value) : null,
                    });
                  }}
                />
              </label>
              <label>
                <span>Public phone</span>
                <input
                  type="tel"
                  value={draft.phone ?? ""}
                  onChange={(event) => {
                    setDraft({ ...draft, phone: event.target.value || null });
                  }}
                />
              </label>
              <label>
                <span>Public website</span>
                <input
                  type="url"
                  value={draft.publicWebsite ?? ""}
                  onChange={(event) => {
                    setDraft({ ...draft, publicWebsite: event.target.value || null });
                  }}
                />
              </label>
              <label className="is-wide">
                <span>Landmark note</span>
                <input
                  maxLength={240}
                  value={draft.landmarkNote ?? ""}
                  onChange={(event) => {
                    setDraft({ ...draft, landmarkNote: event.target.value || null });
                  }}
                  placeholder="Opposite the main entrance"
                />
              </label>
              <label className="is-wide">
                <span>Weekday hours</span>
                <input
                  value={draft.openingHours.monday ?? ""}
                  onChange={(event) => {
                    const hours = event.target.value;
                    setDraft({
                      ...draft,
                      openingHours: {
                        ...draft.openingHours,
                        monday: hours,
                        tuesday: hours,
                        wednesday: hours,
                        thursday: hours,
                        friday: hours,
                      },
                    });
                  }}
                  placeholder="09:00–18:00"
                />
              </label>
            </div>
            <label className="lx-check">
              <input
                type="checkbox"
                checked={draft.isPublic}
                onChange={(event) => {
                  setDraft({ ...draft, isPublic: event.target.checked });
                }}
              />
              <span>
                <strong>Show this location publicly</strong>
                <small>
                  Customers can see the public address, hours, phone, website, and confirmed
                  coordinates.
                </small>
              </span>
            </label>
            <button
              className="button button--primary button--full"
              disabled={save.isPending}
              type="submit"
            >
              <MapPin />{" "}
              {save.isPending
                ? "Saving location…"
                : editingId
                  ? "Save changes"
                  : "Publish location"}
            </button>
          </form>
          <aside className="portal-panel lx-pin-preview">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">Pin confirmation</p>
                <h2>Is this the correct entrance?</h2>
                <p>
                  Adjust the exact coordinates if the geocoder placed the marker on the wrong side
                  of the building.
                </p>
              </div>
            </div>
            <div className="lx-admin-map">
              {draft.latitude !== null && draft.longitude !== null ? (
                <iframe
                  title="Location pin preview"
                  loading="lazy"
                  key={`${String(draft.latitude)}-${String(draft.longitude)}`}
                  src={mapEmbed(draft.latitude, draft.longitude)}
                />
              ) : (
                <div className="lx-map-empty lx-map-empty--compact">
                  <MapPin />
                  <strong>No pin selected</strong>
                  <p>Search for an address or enter exact coordinates.</p>
                </div>
              )}
              <span>© OpenStreetMap contributors</span>
            </div>
            <div className="lx-coordinate-readout">
              <Crosshair />
              <div>
                <strong>
                  {draft.latitude !== null && draft.longitude !== null
                    ? `${draft.latitude.toFixed(6)}, ${draft.longitude.toFixed(6)}`
                    : "Text address only"}
                </strong>
                <small>{draft.addressLine1 || "Search for an address to set the pin"}</small>
              </div>
              <Check />
            </div>
          </aside>
        </div>
      )}
      {!formOpen && (
        <section className="portal-panel lx-location-list">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Published branches</p>
              <h2>{String(locations.data?.length ?? 0)} locations</h2>
            </div>
          </div>
          {locations.isLoading ? (
            <ExperienceState
              title="Loading locations"
              detail="Checking current public map entries."
            />
          ) : locations.isError ? (
            <ExperienceState
              title="Locations could not be loaded"
              detail={messageFromError(locations.error)}
              retry={() => {
                void locations.refetch();
              }}
            />
          ) : locations.data?.length ? (
            <div className="lx-branch-list">
              {locations.data.map((location) => (
                <article key={location.publicId}>
                  <span>
                    <Building2 />
                  </span>
                  <div>
                    <strong>{location.name}</strong>
                    <p>{address(location)}</p>
                    <small>
                      {location.code} · {location.isPublic ? "Public" : "Hidden"} ·{" "}
                      {location.status}
                    </small>
                  </div>
                  <button
                    className="button button--quiet button--compact"
                    type="button"
                    onClick={() => {
                      edit(location);
                    }}
                  >
                    <Pencil /> Edit
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="lx-empty">
              <MapPin />
              <h3>No mapped locations</h3>
              <p>Add the first branch so customers can find and navigate to you.</p>
            </div>
          )}
        </section>
      )}
    </main>
  );
}

export function TenantScanner({
  close,
  onMemberSelected,
}: {
  close: () => void;
  onMemberSelected: (member: {
    publicId: string;
    displayName: string;
    profileImageUrl: string | null;
    memberNumber: string;
    tierCode: string;
    tierName: string;
  }) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const busyRef = useRef(false);
  const lastScanRef = useRef({ value: "", at: 0 });
  const [devices, setDevices] = useState<Array<{ deviceId: string; label: string }>>([]);
  const [deviceId, setDeviceId] = useState("");
  const [cameraState, setCameraState] = useState<
    "idle" | "starting" | "active" | "denied" | "error"
  >("idle");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [scan, setScan] = useState<Awaited<ReturnType<typeof resolveMemberScan>> | null>(null);
  const resolve = useMutation({
    mutationFn: resolveMemberScan,
    onSuccess: (value) => {
      setScan(value);
      setErrorMessage("");
      setMessage("Member found. Confirm before continuing to checkout.");
    },
    onError: (error) => {
      setMessage("");
      setErrorMessage(messageFromError(error));
    },
    onSettled: () => {
      busyRef.current = false;
    },
  });
  const confirm = useMutation({
    mutationFn: confirmMemberScan,
    onSuccess: (value) => {
      setErrorMessage("");
      controlsRef.current?.stop();
      controlsRef.current = null;
      onMemberSelected(value.customer);
      setScan(null);
      close();
    },
    onError: (error) => {
      setMessage("");
      setErrorMessage(messageFromError(error));
    },
  });
  const handleCode = (value: string) => {
    const clean = value.trim();
    const now = Date.now();
    if (
      !clean ||
      busyRef.current ||
      (lastScanRef.current.value === clean && now - lastScanRef.current.at < 3_000)
    )
      return;
    lastScanRef.current = { value: clean, at: now };
    busyRef.current = true;
    resolve.mutate(clean);
  };
  const stop = () => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    setCameraState("idle");
  };
  const closeScanner = () => {
    stop();
    close();
  };
  const start = async (preferredDevice = deviceId) => {
    stop();
    setCameraState("starting");
    setMessage("");
    setErrorMessage("");
    setWarningMessage("");
    try {
      const { BrowserQRCodeReader } = await import("@zxing/browser");
      if (!videoRef.current) throw new Error("The camera preview could not be opened.");
      const reader = new BrowserQRCodeReader(undefined, { delayBetweenScanAttempts: 250 });
      const onResult = (result: { getText: () => string } | undefined) => {
        if (result) handleCode(result.getText());
      };
      if (preferredDevice) {
        try {
          controlsRef.current = await reader.decodeFromVideoDevice(
            preferredDevice,
            videoRef.current,
            onResult,
          );
        } catch (error) {
          if (!canRetryWithAutomaticCamera(error)) throw error;
          controlsRef.current = await reader.decodeFromConstraints(
            rearCameraConstraints,
            videoRef.current,
            onResult,
          );
        }
      } else {
        controlsRef.current = await reader.decodeFromConstraints(
          rearCameraConstraints,
          videoRef.current,
          onResult,
        );
      }

      // Device names and IDs are often hidden on mobile until camera permission is granted.
      // Populate the switcher only after the first stream has successfully opened.
      const activeDevice = activeCameraDeviceId(videoRef.current);
      try {
        const options = cameraOptions(await BrowserQRCodeReader.listVideoInputDevices());
        setDevices(options);
        setDeviceId(activeDevice || preferredDevice || options[0]?.deviceId || "");
      } catch {
        // Enumeration is optional once the stream is running; scanning must keep working even
        // when a browser does not expose its camera list.
        setDevices([]);
        setDeviceId(activeDevice || preferredDevice);
      }
      setCameraState("active");
    } catch (error) {
      const failure = describeCameraFailure(
        error,
        "Check camera permission and try the secure QR again.",
      );
      setCameraState(failure.state);
      setWarningMessage(failure.message);
    }
  };
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        controlsRef.current?.stop();
        controlsRef.current = null;
        close();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
      controlsRef.current?.stop();
    };
  }, [close]);
  return (
    <div
      className="checkout-member-scanner"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-member-scanner-title"
    >
      <button
        className="lx-detail__scrim"
        type="button"
        aria-label="Close member scanner"
        onClick={closeScanner}
      />
      <section className="checkout-member-scanner__dialog">
        <header>
          <div>
            <p className="eyebrow">Record a purchase</p>
            <h2 id="checkout-member-scanner-title">Scan customer membership QR</h2>
            <p>
              Scan the customer’s fresh Mero Perks QR, confirm their identity, then continue
              entering the purchase.
            </p>
          </div>
          <button autoFocus type="button" aria-label="Close member scanner" onClick={closeScanner}>
            <X />
          </button>
        </header>
        {message && (
          <div className="lx-message" role="status">
            {message}
          </div>
        )}
        {errorMessage && (
          <AppAlert
            message={errorMessage}
            onDismiss={() => {
              setErrorMessage("");
            }}
          />
        )}
        {warningMessage && (
          <AppAlert
            tone="warning"
            message={warningMessage}
            onDismiss={() => {
              setWarningMessage("");
            }}
          />
        )}
        <div className="lx-scanner-grid">
          <section className="portal-panel lx-scanner">
            <div className="lx-camera">
              <video
                ref={videoRef}
                muted
                playsInline
                aria-label="Live camera preview for a Mero Perks member QR code"
              />
              <div className="lx-camera-frame">
                <span />
                <span />
                <span />
                <span />
                <p>Place the QR code inside the frame</p>
              </div>
              {cameraState !== "active" && (
                <div className="lx-camera-placeholder">
                  {cameraState === "starting" ? (
                    <LoaderCircle className="lx-spin" />
                  ) : cameraState === "denied" ? (
                    <VideoOff />
                  ) : (
                    <Camera />
                  )}
                  <strong>
                    {cameraState === "starting"
                      ? "Starting camera…"
                      : cameraState === "denied"
                        ? "Camera permission denied"
                        : "Camera is off"}
                  </strong>
                  <p>We only process codes while this scanner is open.</p>
                </div>
              )}
            </div>
            <div className="lx-camera-controls">
              {devices.length > 1 && (
                <label>
                  <span className="sr-only">Choose camera</span>
                  <select
                    value={deviceId}
                    onChange={(event) => {
                      setDeviceId(event.target.value);
                      void start(event.target.value);
                    }}
                  >
                    {devices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button
                className="button button--primary"
                type="button"
                disabled={cameraState === "starting"}
                onClick={() => {
                  if (cameraState === "active") stop();
                  else void start();
                }}
              >
                {cameraState === "active" ? (
                  <>
                    <VideoOff /> Stop camera
                  </>
                ) : (
                  <>
                    <Camera /> Enable camera
                  </>
                )}
              </button>
            </div>
          </section>
        </div>
        {scan && (
          <div
            className="lx-scan-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="scan-confirm-title"
          >
            <button
              className="lx-detail__scrim"
              type="button"
              aria-label="Cancel member confirmation"
              onClick={() => {
                setScan(null);
              }}
            />
            <section>
              <span className="lx-scan-success">
                <Check />
              </span>
              <p className="eyebrow">MEMBER FOUND</p>
              <h2 id="scan-confirm-title">{scan.customer.displayName}</h2>
              <PlatformTierCard
                className="lx-scan-tier-card"
                tierCode={scan.customer.tierCode}
                tierName={scan.customer.tierName}
                variant="thumbnail"
              >
                <div className="lx-scan-tier-card__copy">
                  <small>Mero status</small>
                  <strong>{scan.customer.tierName}</strong>
                  <span>Member {scan.customer.memberNumber}</span>
                </div>
              </PlatformTierCard>
              <p>
                Confirm this is the customer in front of you. Then continue to Mero Checkout to
                record a purchase or select a reward.
              </p>
              <div>
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={() => {
                    setScan(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="button button--primary"
                  disabled={confirm.isPending}
                  type="button"
                  onClick={() => {
                    confirm.mutate(scan.sessionToken);
                  }}
                >
                  {confirm.isPending ? "Confirming…" : "Use member for checkout"}
                  <ArrowRight />
                </button>
              </div>
            </section>
          </div>
        )}
      </section>
    </div>
  );
}
